import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import * as React from "react";
import {
  BriefcaseBusiness,
  ChevronsUpDown,
  Loader2,
  Plus,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import type { Brand } from "./AppSidebar";
import { AddBrandDialog } from "./AddBrandDialog";
import { AddBrandTrialDialog } from "./AddBrandTrialDialog";
import { useAuth } from "@/components/AuthContext";
import {
  authAPI,
  billingAPI,
  brandsAPI,
  configAPI,
  type MeResponse,
  type Plan,
} from "@/services/api";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import BrandAction from "./BrandActions";
import { AddBrandLimitDialog } from "./AddBrandLimit";
import { canonicalizeDomain } from "@/utils/domain";
import { useEffect } from "react";
import { useDataStore } from "@/store/useDataStore";
import type { AxiosError } from "axios";
import {
  getWorkspaceMemberLabel,
  isWorkspaceMemberTier,
} from "@/lib/memberTier";

export const getUserPlan = (plans: Plan[], user: MeResponse["user"] | null) => {
  return plans?.find((p: { id: any }) => `${p.id}` === user?.tier);
};

export const PlanSwitcher: React.FC<{
  brands: Brand[];
  company: any;
  companies?: any[];
  setBrands: (brands: Brand[]) => void;
  isTrialEnded: boolean;
  defaultBrand: Brand;
  setCurrentBrand: (brand: Brand) => void;
  setCurrentCompany?: (company: any) => void;
}> = React.memo(
  ({
    brands,
    company,
    companies = [],
    setBrands,
    defaultBrand,
    isTrialEnded,
    setCurrentBrand,
    setCurrentCompany = () => {},
  }) => {
    const { t } = useLocale();
    const [open, setOpen] = React.useState<boolean>(false);
    const { user } = useAuth();
    const normalizedTier = String(user?.tier || "")
      .trim()
      .toLowerCase();
    const activeAccessRole = defaultBrand?.accessRole || company?.role;
    const isBrandMember = activeAccessRole
      ? activeAccessRole === "brand_member"
      : normalizedTier === "collaborator";
    const isCompanyMember = activeAccessRole
      ? activeAccessRole === "company_member"
      : normalizedTier === "company_member";
    const isCollaborator = activeAccessRole
      ? activeAccessRole !== "company_admin"
      : isWorkspaceMemberTier(user?.tier);
    const [dropDownOpened, setDropDownOpened] = React.useState(false);
    const [brandToDelete, setBrandToDelete] = React.useState<string | null>(
      null,
    );
    const [isDeletingBrand, setIsDeletingBrand] = React.useState(false);
    const deletionInFlight = React.useRef(false);
    const switcherTrigger = React.useRef<HTMLButtonElement>(null);
    const [isCreationInProgress, setIsCreationInProgress] =
      React.useState(false);
    const queryClient = useQueryClient();
    const reset = useDataStore((state) => state.reset);
    useEffect(() => {
      if (!open) {
        reset();
      }
    }, [open]);

    const handleDelete = async (id: string) => {
      if (deletionInFlight.current) return;
      if (brands.length <= 1) {
        toast.error(t("legacy.you.must.keep.at.least.one.brand.ukpbb"), {
          icon: (
            <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
              <TriangleAlert className="w-4 h-4 text-black" />
            </div>
          ),
          position: "bottom-right",
        });
        return;
      }
      deletionInFlight.current = true;
      setIsDeletingBrand(true);
      try {
        await brandsAPI.deleteBrand(id);
        const filtered = brands.filter(
          (brand) => (brand.brandId || brand.id) !== id,
        );
        setBrands(filtered);
        if ((defaultBrand.brandId || defaultBrand.id) === id && filtered[0]) {
          setCurrentBrand(filtered[0]);
        }
        setBrandToDelete(null);
        toast.success(t("legacy.the.brand.was.deleted.successfully.kzwn3r"), {
          icon: (
            <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
              <Trash2 className="w-4 h-4 text-black" />
            </div>
          ),
          position: "bottom-right",
        });
        // Refresh usage independently: a slow read must not hide a successful delete.
        void Promise.all([
          queryClient.invalidateQueries({ queryKey: ["limits"] }),
          queryClient.invalidateQueries({ queryKey: ["me-usage"] }),
        ]).catch(() => {});
      } catch (err: unknown) {
        const message =
          (err as AxiosError<{ message?: string }>)?.response?.data?.message ||
          "Could not delete brand";
        toast.error(message, {
          icon: (
            <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
              <TriangleAlert className="w-4 h-4 text-black" />
            </div>
          ),
          position: "bottom-right",
        });
      } finally {
        deletionInFlight.current = false;
        setIsDeletingBrand(false);
      }
    };

    const { data: plans = [], isLoading } = useQuery({
      queryKey: ["getPlans"],
      queryFn: async () => {
        const result = await configAPI.getPlans();
        return result || [];
      },
      staleTime: Infinity,
    });
    const { data: billingSubscription } = useQuery({
      queryKey: ["billing-subscription", user?.id ?? "me"],
      queryFn: billingAPI.getSubscription,
      enabled: !isCollaborator && Boolean(user?.id),
      staleTime: 60_000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });
    const { data: meUsage } = useQuery({
      queryKey: ["me-usage", user?.id ?? "me"],
      queryFn: authAPI.getMeUsage,
      enabled: !isBrandMember && Boolean(user?.id),
      staleTime: 60_000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });
    const hasBillingStatus = Boolean(billingSubscription?.subscription_status);
    const showLegacyTrial = !hasBillingStatus && user?.tier === "trial";
    const memberLabel =
      activeAccessRole === "brand_member"
        ? "Brand Member"
        : activeAccessRole === "company_member"
          ? "Company Member"
          : activeAccessRole === "company_admin"
            ? null
            : getWorkspaceMemberLabel(user?.tier);
    const currentPlanLabel = memberLabel
      ? memberLabel
      : getUserPlan(plans, user)?.name ||
        (showLegacyTrial || !isTrialEnded ? "Trial" : "");

    const isDisabled = () => {
      const usedBrands = Number((meUsage as any)?.brands?.used);
      const limitBrands = Number((meUsage as any)?.brands?.limit);
      if (Number.isFinite(usedBrands) && Number.isFinite(limitBrands)) {
        return usedBrands >= limitBrands;
      }
      if (isCompanyMember) {
        return false;
      }
      const plan = getUserPlan(plans, user);
      return brands.length >= (plan?.limits["brands.max"] || 1);
    };
    const defaultBrandDomain = canonicalizeDomain(defaultBrand?.url);
    const getBrandDomain = (brand: Brand | null | undefined) =>
      canonicalizeDomain(brand?.url) || brand?.url || "";
    const shouldShowBrandDetails = React.useMemo(() => {
      const companyIds = new Set<string>();
      const brandNameCounts = new Map<string, number>();
      brands.forEach((brand) => {
        const companyId =
          brand.companyId || brand.company?.id || brand.company?.companyId;
        if (companyId) {
          companyIds.add(companyId);
        }
        const normalizedName = String(brand.name || "")
          .trim()
          .toLowerCase();
        if (normalizedName) {
          brandNameCounts.set(
            normalizedName,
            (brandNameCounts.get(normalizedName) || 0) + 1,
          );
        }
      });
      const activeCompanyId = company?.id || company?.companyId;
      if (activeCompanyId) {
        companyIds.add(activeCompanyId);
      }
      return (
        companyIds.size > 1 ||
        Array.from(brandNameCounts.values()).some((count) => count > 1)
      );
    }, [brands, company]);

    return (
      <SidebarMenu>
        <SidebarMenuItem className="flex justify-center">
          <DropdownMenu open={dropDownOpened} onOpenChange={setDropDownOpened}>
            <DropdownMenuTrigger asChild className="cursor-pointer">
              <SidebarMenuButton
                ref={switcherTrigger}
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <div className="bg-sidebar-primary-foreground text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                  <img
                    className="h-8 w-8 shrink-0 rounded-md object-contain"
                    src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${defaultBrandDomain || ""}&size=64`}
                    alt={`Current ${defaultBrand?.name || "brand"} logo`}
                  />
                </div>
                <div className="flex flex-col gap-0.5 leading-none">
                  <span className="font-medium capitalize">
                    {defaultBrand?.name || company?.name || "Loading..."}
                  </span>
                  {isLoading ? (
                    <span className="text-sidebar-ring text-xs">
                      <LocalizedText id="legacy.loading.1ooa6x9" />
                    </span>
                  ) : (
                    <span className="text-sidebar-ring text-xs">
                      {currentPlanLabel}
                    </span>
                  )}
                </div>
                <ChevronsUpDown className="ml-auto" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              onCloseAutoFocus={(event) => {
                if (brandToDelete) event.preventDefault();
              }}
              side="right"
              className="w-(--radix-dropdown-menu-trigger-width) px-3 pt-4"
              align="start"
            >
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-4 capitalize">
                <div className="p-1 rounded-md border">
                  <BriefcaseBusiness size={16} />
                </div>
                {defaultBrand?.name || company?.name || "Loading..."}
                {shouldShowBrandDetails && getBrandDomain(defaultBrand) ? (
                  <span className="ml-auto max-w-[140px] truncate text-xs normal-case">
                    {getBrandDomain(defaultBrand)}
                  </span>
                ) : null}
              </div>
              <Separator />
              {companies.length > 1 ? (
                <div className="flex flex-col gap-2 py-3">
                  <p className="text-xs text-muted-foreground">
                    Workspaces
                  </p>
                  {companies.map((workspace) => (
                    <DropdownMenuItem
                      key={workspace.id || workspace.companyId}
                      onClick={() => setCurrentCompany(workspace)}
                      className="cursor-pointer gap-2 p-1 text-sm font-medium"
                    >
                      <BriefcaseBusiness size={16} />
                      <span className="truncate">{workspace.name}</span>
                    </DropdownMenuItem>
                  ))}
                  <Separator />
                </div>
              ) : null}
              <div className="flex gap-2 flex-col">
                <p className="text-xs text-muted-foreground mt-4">
                  <LocalizedText id="legacy.your.brands.1a49f0c" />
                </p>
                {brands.map((brand) => (
                  <DropdownMenuItem
                    onClick={() => setCurrentBrand(brand)}
                    className="p-0 flex justify-between font-medium text-sm capitalize cursor-pointer"
                    key={brand.brandId}
                  >
                    <div className="flex min-w-0 gap-3">
                      <img
                        className="h-5 w-5 shrink-0 rounded-md border object-contain"
                        src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${canonicalizeDomain(brand?.url) || ""}&size=64`}
                        alt={`${brand.name} logo`}
                      />
                      <span className="min-w-0">
                        <span className="block truncate">{brand.name}</span>
                        {shouldShowBrandDetails &&
                        (brand.company?.name || getBrandDomain(brand)) ? (
                          <span className="block truncate text-xs font-normal normal-case text-muted-foreground">
                            {brand.company?.name || getBrandDomain(brand)}
                          </span>
                        ) : null}
                      </span>
                    </div>
                    {!isCollaborator && (
                      <BrandAction
                        setDropDownOpened={setDropDownOpened}
                        onRequestDelete={setBrandToDelete}
                        onOpenSettings={() => setCurrentBrand(brand)}
                        brand={brand}
                        canDeleteBrand={brands.length > 1}
                      ></BrandAction>
                    )}
                  </DropdownMenuItem>
                ))}
                <Separator />
              </div>
              {isBrandMember ? (
                <p className="text-xs text-muted-foreground py-3">
                  <LocalizedText id="legacy.brand.management.is.handled.by.your.workspace.owner.e64bkp" />
                </p>
              ) : (
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="ghost"
                      className="pr-4 pl-0 cursor-pointer w-full justify-start text-muted-foreground font-medium text-sm"
                      onClick={() => {
                        setOpen(true);
                        !isDisabled() && setIsCreationInProgress(true);
                      }}
                    >
                      <div className="border p-1 rounded-lg">
                        <Plus />
                      </div>
                      <LocalizedText id="legacy.add.brand.6m2svz" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent
                    showCloseButton={false}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    className={`${!isTrialEnded && "gap-2"} ${isDisabled() && !isCreationInProgress && "sm:max-w-[423px]"}`}
                  >
                    <DialogHeader>
                      <DialogTitle className="sr-only">
                        <LocalizedText id="legacy.brand.and.prompt.setup.1qcow9i" />
                      </DialogTitle>
                    </DialogHeader>
                    {isTrialEnded ? (
                      <AddBrandTrialDialog
                        setOpen={setOpen}
                      ></AddBrandTrialDialog>
                    ) : isDisabled() && !isCreationInProgress ? (
                      <AddBrandLimitDialog
                        setDropDownOpened={setDropDownOpened}
                        setOpen={setOpen}
                      ></AddBrandLimitDialog>
                    ) : (
                      <AddBrandDialog
                        setDropDownOpened={setDropDownOpened}
                        setCurrentBrand={setCurrentBrand}
                        setBrands={setBrands}
                        setIsCreationInProgress={setIsCreationInProgress}
                        brands={brands}
                        defaultBrand={defaultBrand}
                        setOpen={setOpen}
                        company={company}
                      ></AddBrandDialog>
                    )}
                  </DialogContent>
                </Dialog>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <AlertDialog
            open={brandToDelete !== null}
            onOpenChange={(isOpen) => {
              if (!isOpen && !deletionInFlight.current) setBrandToDelete(null);
            }}
          >
            <AlertDialogContent
              aria-busy={isDeletingBrand}
              onCloseAutoFocus={(event) => {
                event.preventDefault();
                switcherTrigger.current?.focus();
              }}
            >
              <AlertDialogHeader>
                <AlertDialogTitle className="text-center">
                  <LocalizedText id="legacy.delete.this.brand.1h8o88u" />
                </AlertDialogTitle>
                <AlertDialogDescription className="text-center">
                  <LocalizedText id="legacy.this.action.will.permanently.remove.all.data.prompts.3l3598" />
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogAction
                  disabled={isDeletingBrand}
                  onClick={(event) => {
                    event.preventDefault();
                    if (brandToDelete) void handleDelete(brandToDelete);
                  }}
                  className="hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
                >
                  {isDeletingBrand && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  <LocalizedText id="legacy.delete.permanently.1std29f" />
                </AlertDialogAction>
                <AlertDialogCancel
                  disabled={isDeletingBrand}
                  className="cursor-pointer"
                >
                  <LocalizedText id="legacy.cancel.18x3nqb" />
                </AlertDialogCancel>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  },
);
